import { useState } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardActionArea,
  Chip,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import {
  PartnerStatsTable,
  PeriodSelect,
  usePartnerStats,
  type PartnerStat,
  type Period,
  type StatsSide,
} from '../PartnerStats';
import { Heading, ListToolbar, money, Props, skuPicker } from '../ui';

const sideUsages: Record<StatsSide, string[]> = {
  SALE: ['SALE', 'BOTH'],
  PURCHASE: ['PURCHASE', 'BOTH'],
};
const sideWords: Record<StatsSide, { total: string; partner: string }> = {
  SALE: { total: '销售额', partner: '客户' },
  PURCHASE: { total: '采购额', partner: '供应商' },
};

/**
 * One side's totals across all items. The card is also the switch: clicking it ranks that
 * side's partners below, so sales and purchases stay comparable at a glance.
 */
function SideCard(p: {
  side: StatsSide;
  rows: PartnerStat[] | null;
  selected: boolean;
  onSelect: () => void;
}) {
  const { total, partner } = sideWords[p.side];
  const amount = p.rows?.reduce((s, r) => s + Number(r.amount), 0) ?? 0;
  const orders = p.rows?.reduce((s, r) => s + r.orders, 0) ?? 0;
  return (
    <Card
      variant="outlined"
      sx={{
        borderWidth: 1.5,
        borderColor: p.selected ? 'primary.main' : 'divider',
        boxShadow: p.selected ? '0 0 0 3px #e8f3f0' : 'none',
        transition: 'border-color .15s, box-shadow .15s',
      }}
    >
      <CardActionArea aria-pressed={p.selected} onClick={p.onSelect} sx={{ px: 2.75, py: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 22 }}>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            {total}
          </Typography>
          {p.selected && (
            <Chip
              label="查看中"
              size="small"
              sx={{ height: 20, fontSize: 11, bgcolor: 'primary.light', color: 'primary.main' }}
            />
          )}
        </Box>
        {p.rows ? (
          <>
            <Typography
              sx={{
                mt: 1,
                fontSize: 30,
                fontWeight: 700,
                letterSpacing: '-0.6px',
                lineHeight: 1.25,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {money(amount)}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mt: 1.25, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}
            >
              <Box component="b" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {orders}
              </Box>{' '}
              单 ·{' '}
              <Box component="b" sx={{ color: 'text.primary', fontWeight: 600 }}>
                {p.rows.length}
              </Box>{' '}
              家{partner}
            </Typography>
          </>
        ) : (
          <>
            <Skeleton width={200} height={38} sx={{ mt: 1 }} />
            <Skeleton width={120} height={20} sx={{ mt: 1 }} />
          </>
        )}
      </CardActionArea>
    </Card>
  );
}

/**
 * Whole-business ranking: which customers buy the most, which suppliers we buy from most.
 * Picking one item adds quantity and average price in its unit.
 */
export function TradeStats(p: Props) {
  const [side, setSide] = useState<StatsSide>('SALE');
  const [sku, setSku] = useState<any>(null);
  const [period, setPeriod] = useState<Period>('YEAR');
  // The cards always show all items; the item filter only narrows the ranking below.
  const sales = usePartnerStats('SALE', undefined, period);
  const purchases = usePartnerStats('PURCHASE', undefined, period);
  const perItem = usePartnerStats(side, sku?.id, period, !!sku);
  const current = sku ? perItem : side === 'SALE' ? sales : purchases;
  const options = (p.bootstrap.skus as any[]).filter((s) => sideUsages[side].includes(s.usage));
  const { partner } = sideWords[side];

  const pick = (next: StatsSide) => {
    if (next === side) return;
    setSide(next);
    // An item belongs to one side's catalogue, so the pick does not carry over.
    setSku(null);
  };

  return (
    <>
      <Heading
        title="交易统计"
        subtitle="按客户和供应商汇总成交金额，看清钱从哪来、花到哪去"
        action={<PeriodSelect value={period} onChange={setPeriod} />}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <SideCard
          side="SALE"
          rows={sales.rows}
          selected={side === 'SALE'}
          onSelect={() => pick('SALE')}
        />
        <SideCard
          side="PURCHASE"
          rows={purchases.rows}
          selected={side === 'PURCHASE'}
          onSelect={() => pick('PURCHASE')}
        />
      </Box>
      <Paper sx={{ overflow: 'hidden' }}>
        <ListToolbar>
          <Box sx={{ pl: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography component="h2" sx={{ fontSize: 15, fontWeight: 650 }}>
              {partner}排行
            </Typography>
            {current.rows && (
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                {current.rows.length} 家
              </Typography>
            )}
          </Box>
          <Box sx={{ py: 1.25 }}>
            <Autocomplete<any>
              size="small"
              options={options}
              value={sku}
              onChange={(_, next) => setSku(next)}
              {...skuPicker()}
              renderInput={(params) => (
                <TextField {...params} placeholder="全部商品，可输入编码或名称" />
              )}
              sx={{ width: 300 }}
            />
          </Box>
        </ListToolbar>
        <PartnerStatsTable side={side} rows={current.rows} error={current.error} unit={sku?.unit} />
      </Paper>
    </>
  );
}
